import { AlertTriangle, Loader2, Check } from 'lucide-react';

export interface LoadingProgress {
  hasToken: boolean;
  hasUserInfo: boolean;
  hasPermissions: boolean;
  hasShortcuts: boolean;
}

export interface SecurityGateProps {
  state: 'checking' | 'loading' | 'invalid';
  /** Detalhes do progresso de carregamento (apenas para state='loading') */
  loadingProgress?: LoadingProgress;
}

/**
 * Página de segurança que bloqueia acesso às ferramentas quando
 * tokens não estão validados ou ausentes.
 * Substitui completamente o ToolsMenu até validação ser bem-sucedida.
 */
export function SecurityGate({ state, loadingProgress }: SecurityGateProps) {
  if (state === 'checking') {
    return (
      <div className="h-screen w-full bg-navy-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-blue-400 animate-spin mx-auto mb-4" />
          <p className="text-xl text-white mb-2">Validando sessão...</p>
          <p className="text-sm text-gray-400">Aguarde enquanto verificamos sua autenticação</p>
        </div>
      </div>
    );
  }

  if (state === 'loading') {
    const progress = loadingProgress;
    const steps = [
      { done: progress?.hasToken ?? false, label: 'Token de sessão' },
      { done: progress?.hasUserInfo ?? false, label: 'Informações do usuário' },
      { done: progress?.hasPermissions ?? false, label: 'Permissões' },
      { done: progress?.hasShortcuts ?? false, label: 'Atalhos de teclado' },
    ];
    const completedSteps = steps.filter(s => s.done).length;
    const progressPercent = (completedSteps / steps.length) * 100;

    return (
      <div className="h-screen w-full bg-navy-900 flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <Loader2 className="w-16 h-16 text-blue-400 animate-spin mx-auto mb-4" />
          <p className="text-xl text-white mb-4">Carregando Ferramentas...</p>
          
          {/* Barra de progresso */}
          <div className="w-full bg-navy-700 rounded-full h-2 mb-4">
            <div 
              className="bg-blue-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          
          {/* Lista de etapas */}
          <div className="text-left space-y-2">
            {steps.map((step, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                {step.done ? (
                  <Check className="w-4 h-4 text-green-400" />
                ) : (
                  <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
                )}
                <span className={step.done ? 'text-gray-400' : 'text-white'}>
                  {step.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-full bg-navy-900 flex items-center justify-center">
      <div className="text-center max-w-md px-6">
        <div className="mb-6">
          <AlertTriangle className="w-20 h-20 text-red-500 mx-auto" />
        </div>
        <h1 className="text-2xl font-bold text-white mb-4">Sessão não autenticada</h1>
        <p className="text-gray-300 mb-6">
          Esta janela foi aberta sem autenticação válida. Para usar as Ferramentas, você precisa:
        </p>
        <div className="bg-navy-800 rounded-lg p-4 text-left">
          <ol className="list-decimal list-inside space-y-2 text-gray-300 text-sm">
            <li>Fazer login na aplicação principal</li>
            <li>Fechar esta janela</li>
            <li>Abrir as Ferramentas novamente pelo menu</li>
          </ol>
        </div>
      </div>
    </div>
  );
}

